import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Box, Typography } from "@material-ui/core";
import {
  FALLBACK_COLORS,
  REACH_COLORS,
} from "../usdsBoxPlot/usdsBoxPlotHelpers";
import { bandsPlugin, hoverGuidePlugin, toBandItems } from "./usdsLineBands";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend
);

/**
 * The annual statistic for each site along one reach, upstream to downstream.
 *
 * The x axis is categorical, one slot per site in stream order, so uneven
 * spacing between sites never implies a distance. A second parameter can be
 * drawn against a right hand axis, which is how nutrients and conductivity get
 * compared on one chart.
 */

const SECONDARY_COLORS = ["#ad1457", "#f48fb1"];

const fmt = (v, digits = 1) =>
  v == null || Number.isNaN(v) ? "--" : Number(v).toFixed(digits);

/** Dashed reference lines for the benchmark, drawn under the data. */
export const benchmarkPlugin = {
  id: "usdsBenchmarks",
  beforeDatasetsDraw(chart, _args, opts) {
    const lines = opts?.lines ?? [];
    if (!lines.length) return;
    const { ctx, chartArea } = chart;
    lines.forEach(({ value, axis, color }) => {
      const scale = chart.scales[axis];
      if (!scale || value == null) return;
      const y = scale.getPixelForValue(value);
      if (y < chartArea.top || y > chartArea.bottom) return;
      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(chartArea.left, y);
      ctx.lineTo(chartArea.right, y);
      ctx.stroke();
      ctx.restore();
    });
  },
};

export default function UsdsLineChart({
  reach,
  statisticLabel,
  hideBenchmark = false,
  landmarks = [],
  scale = "linear",
  height = 420,
}) {
  const primary = useMemo(
    () => reach.series.filter((s) => !s.isSecondary),
    [reach.series]
  );
  const secondary = useMemo(
    () => reach.series.filter((s) => s.isSecondary),
    [reach.series]
  );

  const reachColors = REACH_COLORS[reach.reachGroupid] ?? FALLBACK_COLORS;

  const data = useMemo(() => {
    const build = (series, colors, axisId) =>
      series.map((s, i) => ({
        label: `${s.paramAbbrev} ${s.period}`,
        data: s.data,
        yAxisID: axisId,
        borderColor: colors[i % colors.length],
        backgroundColor: colors[i % colors.length],
        /* The comparison period is dashed so the selected year reads as the
           subject and the comparison as the backdrop. */
        borderDash: s.isComparison ? [6, 4] : [],
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        spanGaps: true,
        tension: 0.15,
        counts: s.counts,
        unitName: s.unitName,
      }));

    return {
      labels: reach.labels,
      datasets: [
        ...build(primary, reachColors, "y"),
        ...build(secondary, SECONDARY_COLORS, "y2"),
      ],
    };
  }, [primary, secondary, reachColors, reach.labels]);

  const benchmarkLines = useMemo(() => {
    const lines = [];
    if (hideBenchmark) return lines;
    const first = primary[0];
    if (first?.benchmarkHigh != null)
      lines.push({ value: first.benchmarkHigh, axis: "y", color: "#c62828" });
    if (first?.benchmarkLow != null)
      lines.push({ value: first.benchmarkLow, axis: "y", color: "#c62828" });
    return lines;
  }, [primary, hideBenchmark]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      scales: {
        x: {
          title: { display: true, text: "Upstream to downstream" },
          ticks: { maxRotation: 45, minRotation: 45 },
        },
        y: {
          /* A zero (a non-detect) has no place on a log axis and is skipped. */
          type: scale === "log" ? "logarithmic" : "linear",
          title: {
            display: true,
            text: primary[0]
              ? `${primary[0].paramAbbrev} (${primary[0].unitName})`
              : statisticLabel,
          },
          beginAtZero: scale !== "log",
        },
        ...(secondary.length
          ? {
              y2: {
                type: scale === "log" ? "logarithmic" : "linear",
                position: "right",
                title: {
                  display: true,
                  text: `${secondary[0].paramAbbrev} (${secondary[0].unitName})`,
                },
                beginAtZero: scale !== "log",
                grid: { drawOnChartArea: false },
              },
            }
          : {}),
      },
      plugins: {
        legend: { position: "top", labels: { boxWidth: 14 } },
        usdsBenchmarks: { lines: benchmarkLines },
        usdsBands: { items: toBandItems(landmarks, "category", reach.labels) },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const ds = ctx.dataset;
              const n = ds.counts?.[ctx.dataIndex];
              const unit =
                ds.unitName && ds.unitName !== "None" ? ` ${ds.unitName}` : "";
              return `${ds.label}: ${fmt(ctx.parsed.y)}${unit}${
                n ? ` (n = ${n})` : ""
              }`;
            },
          },
        },
      },
    }),
    [
      primary,
      secondary,
      statisticLabel,
      benchmarkLines,
      scale,
      landmarks,
      reach.labels,
    ]
  );

  const ariaLabel = useMemo(() => {
    const s = primary[0];
    if (!s) return `${reach.reachLabel} reach chart`;
    const points = s.data.filter((v) => v != null);
    if (points.length < 2)
      return `${s.paramDescrip} ${statisticLabel} along ${reach.reachLabel}.`;
    const direction =
      points[points.length - 1] > points[0] ? "increases" : "decreases";
    return (
      `${s.paramDescrip} ${statisticLabel} along ${reach.reachLabel}, ` +
      `${reach.labels.length} sites upstream to downstream. The value ` +
      `${direction} from ${fmt(points[0])} to ${fmt(
        points[points.length - 1]
      )} ` +
      `${s.unitName}.`
    );
  }, [primary, reach, statisticLabel]);

  if (!reach.series.length) {
    return (
      <Box
        height={height}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="body2" color="textSecondary">
          No results for {reach.reachLabel} in this period.
        </Typography>
      </Box>
    );
  }

  return (
    <Box height={height} role="img" aria-label={ariaLabel}>
      <Line
        data={data}
        options={options}
        plugins={[bandsPlugin, benchmarkPlugin, hoverGuidePlugin]}
      />
    </Box>
  );
}
